import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import A_Input from '@/components/A_Atoms/A_Input/A_Input';
import A_Button from '@/components/A_Atoms/A_Button/A_Button';
import A_CheckboxItem from '@/components/A_Atoms/A_CheckboxItem/A_CheckboxItem';
import { useLanguage } from '@/contexts/LanguageContext';
import { getTranslation } from '@/constants/translations';
import { PATHS } from '@/constants/paths';
import styles from './W_AuthForm.module.css';
import cn from 'classnames';
// import W_SocialMedia from '../W_SocialMedia/W_SocialMedia';

interface W_AuthFormProps {
  variant: 'login' | 'register';
  onSubmit: (
    email: string,
    password: string,
    name?: string,
    acceptTerms?: boolean
  ) => void;
  onSocialAuth?: (provider: string) => void;
  onError?: (error: string | null) => void;
  isLoading: boolean;
  error?: string;
  emailNotVerified?: boolean;
  unverifiedEmail?: string;
  resendLoading?: boolean;
  resendDone?: boolean;
  onResendVerification?: (email: string) => void;
}

export default function W_AuthForm({
  variant,
  onSubmit,
  onSocialAuth,
  onError,
  isLoading,
  error,
  emailNotVerified,
  unverifiedEmail,
  resendLoading,
  resendDone,
  onResendVerification,
}: W_AuthFormProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { language } = useLanguage();

  useEffect(() => {
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, []);

  const handleResendClick = () => {
    if (resendCooldown > 0 || resendLoading) return;
    onResendVerification?.(unverifiedEmail || '');
    setResendCooldown(60);
    cooldownRef.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current!);
          cooldownRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const t = (key: string) => getTranslation(language, key);

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (emailError) setEmailError('');
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    if (passwordError) setPasswordError('');
  };

  const handleTermsChange = (checked: boolean) => {
    setAcceptTerms(checked);
  };

  const validateForm = () => {
    let isValid = true;

    if (!email) {
      setEmailError(t('validation.emailRequired'));
      isValid = false;
    }

    if (!password) {
      setPasswordError(t('validation.passwordRequired'));
      isValid = false;
    } else if (password.length < 8) {
      const errorMessage = t('validation.passwordMinLength');
      setPasswordError(errorMessage);
      onError?.(errorMessage);
      isValid = false;
    }

    if (variant === 'register' && !acceptTerms) {
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      if (variant === 'register') {
        onSubmit(email, password, undefined, acceptTerms);
      } else {
        onSubmit(email, password);
      }
    }
  };

  const buttonText = variant === 'login' ? t('auth.login') : t('auth.register');

  const isButtonDisabled =
    isLoading ||
    !email.trim() ||
    !password.trim() ||
    (variant === 'register' && !acceptTerms);

  return (
    <div className={styles.wrapper}>
      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.interactive_inputs_wrapper}>
          <div className={styles.inputs_wrapper}>
            <A_Input
              label={t('auth.email')}
              type='email'
              value={email}
              onChange={handleEmailChange}
              required
              error={emailError}
            />

            <A_Input
              label={t('auth.password')}
              type='password'
              value={password}
              onChange={handlePasswordChange}
              required
              error={passwordError}
            />
          </div>
          {variant === 'login' && (
            <Link
              to={PATHS.FORGOT_PASSWORD}
              className={cn(styles.forgot_password, 'text-p-5')}
            >
              {t('auth.forgotPassword')}
            </Link>
          )}
        </div>

        <div className={styles.button_wrapper}>
          <A_Button
            variant='primary'
            size='large'
            type='submit'
            disabled={isButtonDisabled}
          >
            {buttonText}
          </A_Button>
          {variant === 'register' && (
            <A_CheckboxItem checked={acceptTerms} onChange={handleTermsChange}>
              {t('auth.terms_agreement')}{' '}
              <Link to={PATHS.TERMS} className='text-p-5'>
                {t('auth.terms_of_use')} {t('auth.and')}{' '}
                {t('auth.privacy_policy')}
              </Link>
              .
            </A_CheckboxItem>
          )}
        </div>
      </form>

      {emailNotVerified && (
        <div className={styles.resend_block}>
          <p className={cn(styles.resend_hint, 'text-p-5')}>
            {resendDone
              ? t('auth.resendVerificationSent')
              : t('auth.emailNotVerified')}
          </p>
          {!resendDone && (
            <button
              type='button'
              className={cn(styles.resend_button, 'text-p-5')}
              disabled={resendLoading || resendCooldown > 0}
              onClick={handleResendClick}
            >
              {resendLoading
                ? '...'
                : resendCooldown > 0
                  ? `${t('auth.resendVerification')} (${resendCooldown})`
                  : t('auth.resendVerification')}
            </button>
          )}
        </div>
      )}

      {/* <W_SocialMedia /> */}
    </div>
  );
}
